'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { generateId } from '@/libs/id';
import { cn } from '@/libs/tailwind';
import { Button } from '../Button';

export type FilterPill = {
  label: string;
  value?: string;
  id?: string;
  color?: string;
};

export type TableFilterPillsProps = {
  filters: FilterPill[];
  selectedFilter: string | null;
  onFilterChange: (filter: string) => void;
  onPrefetch?: (filter: string) => void;
  className?: string;
};

/**
 * Generic filter pills component for tables with prefetch capability
 */
export const TableFilterPills = ({
  filters,
  selectedFilter,
  onFilterChange,
  onPrefetch,
  className = '',
}: TableFilterPillsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pillsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const handlePrefetch = (filter: string) => {
    if (onPrefetch && filter !== selectedFilter) {
      onPrefetch(filter);
    }
  };

  const checkScrollability = () => {
    if (containerRef.current && pillsRef.current) {
      const { scrollLeft, clientWidth } = containerRef.current;
      const scrollWidth = pillsRef.current.scrollWidth;

      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth + 5 < scrollWidth);
    }
  };

  useEffect(() => {
    checkScrollability();

    const handleResize = () => {
      checkScrollability();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [filters]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.addEventListener('scroll', checkScrollability);
      return () => {
        containerRef.current?.removeEventListener('scroll', checkScrollability);
      };
    }
    return () => {};
  }, []);

  const scrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // If no filters are provided, don't render anything
  if (!filters || filters.length === 0) {
    return null;
  }

  return (
    <div className={`relative flex w-full items-center ${className}`}>
      {canScrollLeft && (
        <Button
          variant="tertiary"
          onClick={scrollLeft}
          className="size-10 items-center justify-center bg-bg-surface-primary absolute left-[-11px] z-10"
          aria-label="Scroll left"
          size="icon"
        >
          <ChevronLeft size={16} />
        </Button>
      )}

      <div
        ref={containerRef}
        className={cn('flex w-full', canScrollLeft || canScrollRight ? 'overflow-x-hidden' : '')}
      >
        <div
          ref={pillsRef}
          className="flex whitespace-nowrap min-h-8"
        >
          {filters.map((filter, index) => {
            const value = filter.id || filter.value || filter.label;
            const isSelected = selectedFilter === value;
            const isFirst = index === 0;
            const isLast = index === filters.length - 1;
            return (
              <Button
                key={value}
                variant="secondary"
                active={isSelected}
                className={cn(
                  'px-3 py-2.5 border-0.5 border-solid border-neutral-20',
                  !isFirst && 'rounded-l-none border-l-0',
                  !isLast && 'rounded-r-none',
                  'text-body-sm-medium',
                )}
                onClick={() => onFilterChange(value)}
                onMouseEnter={() => handlePrefetch(value)}
                type="button"
              >
                {filter.label}
              </Button>
            );
          })}
        </div>
      </div>

      {canScrollRight && (
        <Button
          variant="tertiary"
          onClick={scrollRight}
          className="size-10 items-center justify-center bg-bg-surface-primary absolute right-[-11px] z-10"
          aria-label="Scroll right"
          size="icon"
        >
          <ChevronRight size={16} />
        </Button>
      )}
    </div>
  );
};

/**
 * Loading skeleton for the filter pills component
 */
export const FilterPillsSkeleton = ({ count = 5 }: { count?: number }) => {
  return (
    <div className="overflow-x-auto mb-4">
      <div className="flex whitespace-nowrap">
        {Array.from({ length: count }).map(_ => (
          <div
            key={generateId()}
            className="px-4 py-2 animate-pulse w-24 h-10 border-0.5 border-solid border-neutral-20 bg-gray-200"
          />
        ))}
      </div>
    </div>
  );
};
